import Axios from "axios";
import Vue from "vue";
import moment from "moment";
import i18n from "../../i18n";
import { API_HOST } from "../../config/app-const";
import { ARTICLES } from "../../config/const";
import _ from "lodash";
import { normalize, getUrlParams } from "../../helpers/pagination";
import { filtersToQuery } from '../../helpers/filters';
import { displayDate } from "../../helpers/date";
import { camelToSnack } from "../../helpers/case";
import { removeEmptyProps } from '../../helpers/object';
import localStorage from '../../helpers/localStorage';

function bulkDataToSend(data, articlesIndex = 'articles') {
  if (data.toAllRecords.total === data[articlesIndex].length)
    data.toAllRecords.status = true;
  
  if (data.toAllRecords.status) {
    data[articlesIndex] = [];
    data.toAllRecords.filters = camelToSnack(_.cloneDeep(data.toAllRecords.filters));
  } else {
    data.toAllRecords.filters = {};
  }

  return data;
}

function updateArticlesShowErrors(error) {
  let text = i18n.t("messages.request.error");
  let categories = [];

  if (
    error.response.data &&
    error.response.data.message &&
    error.response.data.message
  ) {
    if (
      typeof error.response.data.message === 'string' &&
      !error.response.data.data.categories &&
      !error.response.data.message.categories
    ) {
      text = error.response.data.message;    
    } else if (error.response.data.message.categories) {
      categories = error.response.data.message.categories;
    } else if (error.response.data.data.categories) {
      categories = error.response.data.data.categories;
    }

    if (categories.length) {
      text = i18n.tc(`articles.form.error.missing_categories`, categories.length);
      categories.forEach(message => text += `<br>${message.name}`)
    }
  } 

  Vue.notify({
    group: "store_notifications",
    type: "error",
    text: text
  }); 
}

const state = {
  articles: {
    isBusy: false,
    loadingMore: false,
    data: null,
    error: null,
    backup: null,
    filters: null,
    isBusyExport: false,
  },
  article: {
    updating: null,
    isBusy: false,
    data: null,
    error: null,
    cancelToken: null,
  },
  article_status: {
    isBusy: false,
    data: null,
    error: null
  },
  group: {
    isBusy: false,
    data: null,
    error: null
  },
  organizations: {
    isBusy: false,
    data: null,
    error: null
  },
  categories: {
    isBusy: false,
    data: null,
    error: null
  },
  sidePanel: {
    isBusy: false,
  },
  coders: {
    isBusy: false,
    data: [],
    pagination: null,
    error: null
  },
  find_results: {
    isBusy: false,
    data: null,
    error: null,
    request: null
  },
  mentions: {
    isBusy: false,
    data: null,
    error: null
  },
  assets: {
    isBusy: false,
    data: null,
    error: null
  },
  articlesRelationshipLines: {
    isBusy: false,
    data: null,
    error: null
  },
  settings: {
    isBusy: false,
    data: null,
    error: null
  }
};

const getters = {
  articlesPagination: state => {
    if (state.articles.data)
      return state.articles.data.pagination;

    return '';
  },
  errors: state => {
    if (state.articles.error)
      return state.articles.error;

    return '';
  },
  articlesTotal: (state, getters) => {
    if (getters.articlesPagination)
      return getters.articlesPagination.totalCount;

    return undefined;
  },
  overTotalLimit: (state, getters) => {
    if (getters.articlesPagination)
      return getters.articlesPagination.more;

    return false;
  },
  allArticles: (state) => {
    if (state.articles.data && state.articles.data.data)
      return state.articles.data.data.articles;

    return [];
  },
  loadingArticlesExport: (state) => {
    return state.articles.isBusyExport;
  }
}

const mutations = {
  LOAD_ARTICLES_BUSY: (state, payload) => {
    state.articles.isBusy = payload;
  },
  LOAD_ARTICLES_SUCCESS: (state, payload) => {
    if (
      state.articles.data &&
      state.articles.data.requested_at &&
      payload.requested_at &&
      payload.requested_at < state.articles.data.requested_at
    ) {
      return;
    }
    state.articles.data = payload;
    state.articles.error = null;
  },
  LOAD_ARTICLES_FAIL: (state, payload) => {
    state.articles.data = null;
    state.articles.error = payload;
  },

  // LOAD_MORE_ARTICLES_BUSY: (state, payload) => {
  //   state.articles.loadingMore = payload;
  // },
  // LOAD_MORE_ARTICLES_SUCCESS: (state, payload) => {
  //   state.articles.data.articles = payload;
  //   state.articles.error = null;
  // },
  // LOAD_MORE_ARTICLES_FAIL: (state, payload) => {
  //   state.articles.error = payload;
  // },

  SEARCH_ARTICLES_BUSY: (state, payload) => {
    state.articles.isBusy = payload;
  },
  SEARCH_ARTICLES_SUCCESS: (state, payload) => {
    state.articles.data.data.articles = payload.data;
    state.articles.data.pagination = payload.pagination;
    state.articles.error = null;
  },
  SEARCH_ARTICLES_FAIL: (state, payload) => {
    state.articles.data.articles = null;
    state.articles.error = payload;
  },

  UPDATE_ARTICLE_BUSY: (state, payload) => {
    state.article.updating = payload;
  },
  UPDATE_ARTICLE_SUCCESS: (state, payload) => {
    state.article.updating = null;
    state.articles.data.data.articles = payload;
  },
  UPDATE_ARTICLE_FAIL: (state) => {
    state.article.updating = null;
  },
  UPDATE_ARTICLES_FILTERS: (state, payload) => {
    state.articles.filters = payload;
  },

  UPDATE_ARTICLES_BACKUP: (state) => {
    state.articles.backup = _.cloneDeep(state.articles.data);
  },

  UPDATE_ARTICLE: (state, payload) => {
    payload.forEach(payloadItem => {
      const article = state.articles.data.data.articles
        .find(item => item.id === payloadItem.id);

      article.assigned_to.id = payloadItem.assigned_to.id;
      article.assigned_to.name = payloadItem.assigned_to.name;
      article.analysis_state = payloadItem.analysis_state;
      article.due_date = payloadItem.due_date;
    });
  },

  ROLLBACK_ARTICLES: (state) => {
    state.articles.data = state.articles.backup;
  },

  // remove or when articles becomes unfiltered (status_id 0)
  REMOVE_ARTICLES(state, payload) {
    state.articles.data.data.articles = state.articles.data.data.articles.filter(article => !payload.includes(article.id) )
  },

  EDIT_PAGINATION_TOTAL(state, payload) {
    state.articles.data.pagination.totalCount = payload
  },

  GROUP_ARTICLES_BUSY: (state, payload) => {
    state.sidePanel.isBusy = payload;
  },
  
  GROUP_ARTICLES_SUCCESS: (state, payload) => {    
    let newGroup = payload;

    state.group.data = newGroup;
    state.group.error = null;

    if (!state.articles.data.data.groups.find(g => g.id == newGroup.id)) {
      state.articles.data.data.groups.push(newGroup);
    }
  },

  GROUP_ARTICLES_FAIL: (state, payload) => {
    state.group.data = null;
    state.group.error = payload;
  },

  //PROJECT SETUP - ARTICLES BY GROUP --------------
  LOAD_ARTICLE_BY_GROUP_BUSY: (state, payload) => {
    state.articles.isBusy = payload;
  },
  LOAD_ARTICLE_BY_GROUP_SUCCESS: (state, payload) => {
    state.articles.data = payload;
    state.articles.error = null;
  },
  LOAD_ARTICLE_BY_TAG_FAIL: (state, payload) => {
    state.articles.error = payload;
    state.articles.data = null;
  },
  //------------------------------------------------

  //PROJECT SETUP - ARTICLES BY TAG --------------
  // LOAD_ARTICLE_BY_TAG_BUSY: (state, payload) => {
  //   state.articles.isBusy = payload;
  // },
  // LOAD_ARTICLE_BY_TAG_SUCCESS: (state, payload) => {
  //   state.articles.data = payload;
  //   state.articles.error = null;
  // },
  // LOAD_ARTICLE_BY_GROUP_FAIL: (state, payload) => {
  //   state.articles.error = payload;
  //   state.articles.data = null;
  // },
  //------------------------------------------------

  DELETE_ARTICLES_BUSY: (state, payload) => {
    // state.articles.isBusy = payload;
    state.sidePanel.isBusy = payload;
  },
  DELETE_ARTICLES_SUCCESS: (state, payload) => {
    let articles = state.articles.data.data.articles;
    if (payload.length > 0) {
      state.articles.data.data.articles = articles.filter(a => !payload.find(p => p.article_id === a.id));
    }
    state.articles.error = null;
  },
  DELETE_ARTICLES_FAIL: (state, payload) => {
    // state.articles.data = null;
    state.articles.error = payload;
  },

  UPDATE_BULK_ARTICLES_BUSY: (state, payload) => {
    state.sidePanel.isBusy = payload;
  },
  UPDATE_BULK_ARTICLES_SUCCESS: (state, payload) => {
    let articles = state.articles.data.data.articles.map((a)=> {
      let updated_article = payload.find(p => p.id === a.id);
      return updated_article ?  updated_article : a;
    });
    state.articles.data.data = Object.assign({}, state.articles.data.data, {articles});    
    state.articles.error = null;
  },
  UPDATE_BULK_ARTICLES_SUCCESS_ALL: (state, payload) => {
    state.articles.data.data.articles.forEach((article, i) => {
      const data = { ...article, ...payload.updated_data };

      state.articles.data.data.articles[i] = data;
    });
    
    state.articles.error = null;
  },
  UPDATE_BULK_ARTICLES_FAIL: (state, payload) => {
    state.articles.error = payload;
  },

  //ARTICLE STATUS
  LOAD_ARTICLES_STATUS_BUSY: (state, payload) => {
    state.article_status.isBusy = payload;
  },
  LOAD_ARTICLES_STATUS_SUCCESS: (state, payload) => {
    state.article_status.data = payload;
    state.article_status.error = null;
  },
  LOAD_ARTICLES_STATUS_FAIL: (state, payload) => {
    state.article_status.data = null;
    state.article_status.error = payload;
  },
  //CODERS LIST
  LOAD_CODERS_BUSY: (state, payload) => {
    state.coders.isBusy = payload;
  },
  LOAD_CODERS_SUCCESS: (state, {data, pagination}) => {
    if (pagination && pagination.currentPage === 1) state.coders.data = []
    data.forEach(item => state.coders.data.push(item))
    state.coders.pagination = pagination;
    state.coders.error = null;
  },
  LOAD_CODERS_FAIL: (state, payload) => {
    state.coders.data = null;
    state.coders.error = payload;
  },

  //ORGANIZATIONS
  LOAD_ORGANIZATIONS_BUSY: (state, payload) => {
    state.organizations.isBusy = payload;
  },
  LOAD_ORGANIZATIONS_SUCCESS: (state, payload) => {
    state.organizations.data = payload;
    state.organizations.error = null;
  },
  LOAD_ORGANIZATIONS_FAIL: (state, payload) => {
    state.organizations.data = null;
    state.organizations.error = payload;
  },
  //-----------------

  //CATEGORIES
  LOAD_ARTICLES_CATEGORIES_BUSY: (state, payload) => {
    state.categories.isBusy = payload;
  },
  LOAD_ARTICLES_CATEGORIES_SUCCESS: (state, payload) => {
    state.categories.data = payload;
    state.categories.error = null;
  },
  LOAD_ARTICLES_CATEGORIES_FAIL: (state, payload) => {
    state.categories.error = payload;
    state.categories.data = null;
  },
  //-----------------

  //MENTIONS
  LOAD_MENTIONS_BUSY: (state, payload) => {
    state.mentions.isBusy = payload;
  },
  LOAD_MENTIONS_SUCCESS: (state, payload) => {
    state.mentions.data = payload;
    state.mentions.error = null;
  },
  LOAD_MENTIONS_FAIL: (state, payload) => {
    state.mentions.data = null;
    state.mentions.error = payload;
  },

  //ASSETS
  LOAD_ASSETS_BUSY: (state, payload) => {
    state.assets.isBusy = payload;
  },
  LOAD_ASSETS_SUCCESS: (state, payload) => {
    state.assets.data = payload;
    state.assets.error = null;
  },
  LOAD_ASSETS_FAIL: (state, payload) => {
    state.assets.data = null;
    state.assets.error = payload;
  },

  ADD_USER_CODER_BUSY: (state, payload) => {
    state.coders.isBusy = payload;
  },
  ADD_USER_CODER_SUCCESS: (state, payload) => {
    state.coders.data.push(payload);
    state.coders.error = null;
  },
  ADD_USER_CODER_FAIL: (state, payload) => {
    state.coders.data = null;
    state.coders.error = payload;
  },
  // CODER PROFILE
  UPDATE_CODER_PROFILE_BUSY: (state, payload) => {
    state.coders.isBusy = payload;
  },
  UPDATE_CODER_PROFILE_SUCCESS: (state, payload) => {
    const x = payload.filter(r => r.id != payload.id);
    state.coders.data = x;
    state.coders.error = null;
  },
  UPDATE_CODER_PROFILE_FAIL: (state, payload) => {
    state.coders.data = null;
    state.coders.error = payload;
  },
  // REMOVE CODER FROM PROJECT
  REMOVE_CODER_FROM_PROJECT_BUSY: (state, payload) => {
    state.coders.isBusy = payload;
  },
  REMOVE_CODER_FROM_PROJECT_SUCCESS: (state, payload) => {
    const x = state.coders.data.filter(r => r.id != payload.id);
    state.coders.data = x;
    state.coders.error = null;
  },
  REMOVE_CODER_FROM_PROJECT_FAIL: (state, payload) => {
    state.coders.data = null;
    state.coders.error = payload;
  },
  //GET FIND RESULTS LIST
  LOAD_FIND_RESULTS_BUSY: (state, payload) => {
    state.find_results.isBusy = payload;
  },
  LOAD_FIND_RESULTS_SUCCESS: (state, payload) => {
    state.find_results.data = payload;
    state.find_results.error = null;
  },
  LOAD_FIND_RESULTS_FAIL: (state, payload) => {
    state.find_results.data = null;
    state.find_results.error = payload;
  },
  //RELATIONSHIP LINES 
  LOAD_RELATIONSHIP_LINES_BUSY: (state, payload) => {
    state.articlesRelationshipLines.isBusy = payload;
  },
  LOAD_RELATIONSHIP_LINES_SUCCESS: (state, payload) => {
    state.articlesRelationshipLines.data = payload;
    state.articlesRelationshipLines.error = null;
  },
  LOAD_RELATIONSHIP_LINES_FAIL: (state, payload) => {
    state.articlesRelationshipLines.data = null;
    state.articlesRelationshipLines.error = payload;
  },

  // SETTINGS 
  LOAD_SETTINGS_BUSY: (state, payload) => {
    state.settings.isBusy = payload;
  },
  LOAD_SETTINGS_SUCCESS: (state, payload) => {
    state.settings.data = payload;
    state.settings.error = null;
  },
  LOAD_SETTINGS_FAIL: (state, payload) => {
    state.settings.data = null;
    state.settings.error = payload;
  },

  //ARTICLE 

  LOAD_ARTICLE_BUSY: (state, payload) => {
    state.article.isBusy = payload.isBusy;
    state.article.cancelToken = payload.cancelToken;
  },
  LOAD_ARTICLE_SUCCESS: (state, payload) => {
    state.article = payload;
    state.article.error = null;
    state.article.cancelToken = null;
  },
  LOAD_ARTICLE_FAIL: (state, payload) => {
    state.article.data = null;
    state.article.error = payload;
    state.article.cancelToken = null;
  },

  AI_CODE_BUSY: (state, payload) => {
    state.sidePanel.isBusy = payload;
  },
};

const actions = {

  getArticlesByGroup: ({ commit, rootState }, { projectUid, groupId}) => {
    commit("LOAD_ARTICLE_BY_GROUP_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    Axios.get(`${API_HOST}/analysis/${projectUid}/groups/${groupId}/articles`, config)
      .then(response => {
        commit("LOAD_ARTICLE_BY_GROUP_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_ARTICLE_BY_GROUP_FAIL", error);
      })
      .finally(() => commit("LOAD_ARTICLE_BY_GROUP_BUSY", false));
  },

  getArticlesByTag: ({ commit, state, rootState }, { projectId, tagId }) => {
    //commit("LOAD_ARTICLE_BY_TAG_BUSY"), true;

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/${projectId}/tags/${tagId}`, config)
      .then(response => {
        
        //commit("LOAD_ARTICLE_BY_TAG_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        //commit("LOAD_ARTICLE_BY_TAG_FAIL", error);
      })
      .finally(() => commit("LOAD_ARTICLE_BY_TAG_BUSY", false));
  },

  getArticlesByAuthor: ({ commit, state, rootState }, { projectId, authorId }) => {
    // commit("LOAD_ARTICLE_BY_AUTHOR_BUSY"), true;

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/${projectId}/authors/${authorId}`, config)
      // .then(response => {
      //   // commit("LOAD_ARTICLE_BY_AUTHOR_SUCCESS", response.data);
      // })
      // .catch(error => {
      //   Vue.notify({
      //     group: "store_notifications",
      //     type: "error",
      //     text: i18n.t("messages.request.error")
      //   });

      //   console.error(error);
      //   // commit("LOAD_ARTICLE_BY_AUTHOR_FAIL", error);
      // })
      // // .finally(() => commit("LOAD_ARTICLE_BY_TAG_BUSY", false));
  },

  getArticleStatus: ({ commit, rootState }, { projectId }) => {
    commit("LOAD_ARTICLES_STATUS_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/${projectId}/articles/status`, config)
      .then(response => {
        commit("LOAD_ARTICLES_STATUS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_ARTICLES_STATUS_FAIL", error);
      })
      .finally(() => commit("LOAD_ARTICLES_STATUS_BUSY", false));
  },
  getCoders: ({ commit, rootState }, { projectId, pagination, filters }) => {
    commit("LOAD_CODERS_BUSY", true);

    let url = `${API_HOST}/analysis/${projectId}/coders`;
    const query = getUrlParams({...pagination, search: filters });

    if (query) url += `?${query}`;

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

     Axios.get(url, config)
      .then(({data}) => {
        commit("LOAD_CODERS_SUCCESS", data.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_CODERS_FAIL", error);
      })
      .finally(() => { 
        commit("LOAD_CODERS_BUSY", false)
      });
  },
  removeCodersFromProject: ({ commit, rootState }, { projectId, row }) => {
    commit("REMOVE_CODER_FROM_PROJECT_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const body = {
      ...row
    };
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.delete(`${API_HOST}/analysis/${projectId}/coder/${row.id}/unassign`, config)
      .then(response => {
        commit("REMOVE_CODER_FROM_PROJECT_SUCCESS", body);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("REMOVE_CODER_FROM_PROJECT_FAIL", error);
      })
      .finally(() => commit("REMOVE_CODER_FROM_PROJECT_BUSY", false));
  },

  getCategories: ({ commit, rootState }, { projectUid, pagination }) => {
    commit("LOAD_ARTICLES_CATEGORIES_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token= ${accessToken}` }
    };
    let query = '?'
    if(pagination && Object.keys(pagination).length > 0){
       query += getUrlParams(pagination);
    }
    Axios.get(`${API_HOST}/analysis/${projectUid}/categories?${query}`, config)
      .then(response => {
        commit("LOAD_ARTICLES_CATEGORIES_SUCCESS", response.data.data.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_ARTICLES_CATEGORIES_FAIL", error);
      })
      .finally(() => commit("LOAD_ARTICLES_CATEGORIES_BUSY", false));
  },

  getOrganizations: ({ commit, rootState }, { projectId, pagination }) => {
    commit("LOAD_ORGANIZATIONS_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let query = "?";
    if(pagination && Object.keys(pagination).length > 0){
      const normalizedPagination = normalize(pagination);
      Object.keys(normalizedPagination).map((param, i) => {
        const value = encodeURIComponent(normalizedPagination[param]); 
        query += `${param}=${value}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
      });
    }

    return Axios.get(`${API_HOST}/analysis/${projectId}/organizations${query}`, config)
      .then(response => {        
        commit("LOAD_ORGANIZATIONS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_ORGANIZATIONS_FAIL", error);
      })
      .finally(() => commit("LOAD_ORGANIZATIONS_BUSY", false));
  },

  getArticles: ({ commit, rootState }, { pagination, projectId, filters, getAll = false, forceCall = false }) => {
    if (forceCall) commit("LOAD_ARTICLES_BUSY", false);
    commit('UPDATE_ARTICLES_FILTERS', _.cloneDeep(filters));

    if (!state.articles.isBusy) {
      
      const filtersToSend = _.cloneDeep(filters);
      filtersToSend.dateFrom = filters.dateFrom || '';
      filtersToSend.dateTo = filters.dateTo || '';
      

      const { accessToken } = rootState.user.user.data;

      const requestedAt = new Date();

      const config = {
        headers: { Authorization: `Token token=${accessToken}` }
      };

      let query = "?";
      const perPage = _.clone(pagination.perPage);
      const page = _.clone(pagination.page);

      if (getAll) {
        pagination.perPage = perPage * pagination.page;
        pagination.page = 1;
      }

      const normalizedPagination = normalize(pagination);

      Object.keys(normalizedPagination).map((param, i) => {
        query += `${param}=${normalizedPagination[param]}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
      });
      
      if (filtersToSend) {
        const filtersQuery = filtersToQuery(filtersToSend);
        if (filtersQuery != '') {
          query += '&' + filtersQuery;
        }
      }

      // if (filters) {
      //   const filtersQuery = filtersObjectsToQuery(filters);
      //   if (filtersQuery != '') {
      //     query += '&' + filtersQuery;
      //   }
      // }

      return Axios.get(`${API_HOST}/analysis/${projectId}/articles${query}`, config)
        .then(response => {
          const res = response.data;
          let payload = {};
          
          if (pagination.page == 1) {
            payload = res.data;

            if (getAll) {
              payload.pagination.currentPage = page;
              pagination.perPage = perPage;
              pagination.page = page;
            }

          } else {
            payload = JSON.parse(JSON.stringify(state.articles.data));
            payload.pagination = res.data.pagination;
            payload.data.articles = [...payload.data.articles, ...res.data.data.articles];
            payload.data.outlets = [...payload.data.outlets, ...res.data.data.outlets];
          }

          payload.requested_at = requestedAt;

          commit("LOAD_ARTICLES_SUCCESS", payload);
          return query
        })
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("LOAD_ARTICLES_FAIL", error);
        })
        .finally(() => commit("LOAD_ARTICLES_BUSY", false));
    }
  },
  getFindResults: ({ commit, rootState, state }, { projectUid, query, requestSource, filters }) => {
    commit("LOAD_FIND_RESULTS_BUSY", true);

    const filtersQuery = filtersToQuery(filters);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      cancelToken: requestSource.token
    };
    Axios.get(`${API_HOST}/analysis/${projectUid}/articles/find_results?searchstring=${query}&filters${filtersQuery}`, config)
      .then(response => {
        commit("LOAD_FIND_RESULTS_SUCCESS", response.data);
      })
      .catch(error => {
        if (error.message !== 'aborted') {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("LOAD_FIND_RESULTS_FAIL", error);
        }
      })
      .finally(() => commit("LOAD_FIND_RESULTS_BUSY", false));
  },
  getMoreArticles: ({ commit, state, rootState }, { pagination, projectId }) => {
    if (!state.articles.loadingMore) {
      commit("LOAD_MORE_ARTICLES_BUSY", true);

      const { accessToken } = rootState.user.user.data;

      const config = {
        headers: { Authorization: `Token token=${accessToken}` }
      };

      let query = "?";
      const normalizedPagination = normalize(pagination);
      Object.keys(normalizedPagination).map((param, i) => {
        query += `${param}=${normalizedPagination[param]}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
      });

      Axios.get(`${API_HOST}/analysis/${projectId}/articles${query}`, config)
        .then(response => {
          const articles = state.articles.data.articles || [];
          commit("LOAD_MORE_ARTICLES_SUCCESS", [...articles, ...response.data]);
        })
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("LOAD_MORE_ARTICLES_FAIL", error);
        })
        .finally(() => commit("LOAD_MORE_ARTICLES_BUSY", false));
    }
  },

  search: ({ commit, rootState }, { pagination, projectId, filters, unfiltered }) => {
    commit("SEARCH_ARTICLES_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let query = "?";
    const normalizedPagination = normalize(pagination);

    Object.keys(normalizedPagination).map((param, i) => {
      query += `${param}=${normalizedPagination[param]}${i !== Object.keys(normalizedPagination).length - 1 ? '&' : ''}`;
    });

    if (filters) {
      let normalizedFilters = {};

      Object.keys(filters).forEach(k => {
        if (filters[k] != '') {
          if (_.isArray(filters[k])) {
            normalizedFilters[k + '[]'] = filters[k];
          }
          else {
            normalizedFilters[k] = filters[k];
          }
        }
      })

      normalizedFilters = normalize(normalizedFilters);

      query += "&";
      Object.keys(normalizedFilters).map((param, i) => {
        query += `${param}=${normalizedFilters[param]}${i !== Object.keys(normalizedFilters).length - 1 ? '&' : ''}`;
      });
    }

    Axios.get(
      `${API_HOST}/analysis/${projectId}/articles/filter${query}&unfiltered=${unfiltered}`,
      config
    )
      .then(response => {
        const res = response.data;
        let payload = {};

        if (pagination.page == 1) {
          payload = res.data;
        }
        else {
          const articlesList = JSON.parse(JSON.stringify(state.articles.data.data.articles));
          payload.data = [...articlesList, ...res.data];
          payload.pagination = res.pagination;
        }

        commit("SEARCH_ARTICLES_SUCCESS", payload);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("SEARCH_ARTICLES_FAIL", error);
      })
      .finally(() => commit("SEARCH_ARTICLES_BUSY", false));
  },

  update: ({ commit, state, rootState }, { projectUid, record, preUpdate }) => {
    commit("UPDATE_ARTICLE_BUSY", true);
    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...record
    };

    if (preUpdate) {
      commit("UPDATE_ARTICLES_BACKUP");
      commit("UPDATE_ARTICLE", [record]);
    }

    return new Promise ((resolve, reject) => {
      Axios.put(
        `${API_HOST}/analysis/${projectUid}/articles/update`,
        body,
        config
      )
      .then(response => {
          commit("UPDATE_ARTICLE", response.data);
          resolve(response);
      })
      .catch(error => {
        if (preUpdate) commit("ROLLBACK_ARTICLES");

        commit("UPDATE_ARTICLE_FAIL", error);
        updateArticlesShowErrors(error);
        reject(error);
      })
      .finally(() => commit("UPDATE_ARTICLE_BUSY", false));
    })
  },

  group: ({ commit, rootState, getters }, {
    projectUid,
    articles,
    groupId,
    toAllRecords = {
      status: false, filters: {}, total: 0
    }
  }) => {
    commit("GROUP_ARTICLES_BUSY", true);

    const { id, accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    let body = {
      createdBy: id,
      articles,
      groupId,
      new: _.isString(groupId),
      toAllRecords: toAllRecords
    };

    body = bulkDataToSend(body);
    
    return Axios.post(
      `${API_HOST}/analysis/${projectUid}/articles/group`,
      body,
      config
    )
      .then(response => {
        Vue.notify({
          group: "store_notifications",
          type: "success",
          text: i18n.t("messages.articles.added_to_group")
        });
        commit("GROUP_ARTICLES_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("GROUP_ARTICLES_FAIL", error);
      })
      .finally(() => commit("GROUP_ARTICLES_BUSY", false));
  },

  createGroup: ({ commit, rootState }, { projectUid , payload }) => {
    commit("GROUP_ARTICLES_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const body = {
      ...payload
    };

    return Axios.post(
      `${API_HOST}/analysis/${projectUid}/groups/create`,
      body,
      config
    )
      .then(response => {
        // Vue.notify({
        //   group: "store_notifications",
        //   type: "success",
        //   text: i18n.t("messages.articles.added_to_group")
        // });
        commit("GROUP_ARTICLES_SUCCESS", response.data);
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("GROUP_ARTICLES_FAIL", error);
      })
      .finally(() => commit("GROUP_ARTICLES_BUSY", false));
  },

  delete: ({ commit, rootState, getters }, { 
    projectUid, 
    articles, 
    toAllRecords = {
      status: false, filters: {}, total: 0
    }
  }) => {
    commit("DELETE_ARTICLES_BUSY", true);

    const authId = localStorage.getLocalStorage('request_auth_id');

    if (toAllRecords && toAllRecords.status) articles = getters.allArticles;

    let data = {
      articles,
      toAllRecords,
      authId
    }

    data = bulkDataToSend(data);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data: data
    };

    return new Promise((resolve, reject) => {
      Axios.delete(`${API_HOST}/analysis/${projectUid}/articles/delete`, config)
        .then(
          response => {
            Vue.notify({
              group: "store_notifications",
              type: "success",
              text: i18n.t("Deleted")
            });
            commit("DELETE_ARTICLES_SUCCESS", response.data);
            resolve(response.data);
          },
          error => {
            commit("DELETE_ARTICLES_FAIL", error);
            reject(error);
          }
        )
        .catch(error => {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("DELETE_ARTICLES_FAIL", error);
        })
        .finally(() => commit("DELETE_ARTICLES_BUSY", false));
    });
  },
 
  deleteBulkArticlesFromGroup: ({commit ,rootState}, {projectId, groupId, articleIds}) => {
    commit("DELETE_ARTICLES_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
    };
        
    const body =  [
      ...articleIds
    ]

    return Axios.put(`${API_HOST}/analysis/${projectId}/groups/${groupId}/bulk/remove`, body ,config)
      .then(
        response => {
          Vue.notify({
            group: "store_notifications",
            type: "success",
            text: i18n.t("Deleted")
          });
          commit("DELETE_ARTICLES_SUCCESS", response.data);
          return response.data;
        }
      )
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });
        console.error(error);
        commit("DELETE_ARTICLES_FAIL", error);
      })
      .finally(() => commit("DELETE_ARTICLES_BUSY", false));
  },

  getMentions: ({ commit, rootState }, { projectId, articleId }) => {
    commit("LOAD_MENTIONS_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/${projectId}/articles/mentions/${articleId}`, config)
      .then(response => {
        commit("LOAD_MENTIONS_SUCCESS", response.data);
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_MENTIONS_FAIL", error);
      })
      .finally(() => commit("LOAD_MENTIONS_BUSY", false));
  },

  getArticleAsset: ({ commit, rootState }, { projectId, articleId }) => {
    commit("LOAD_ASSETS_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.get(`${API_HOST}/analysis/${projectId}/articles/assets/${articleId}`, config)
      .then(response => {
        commit("LOAD_ASSETS_SUCCESS", response.data);
        return response.data;
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("LOAD_ASSETS_FAIL", error);
      })
      .finally(() => commit("LOAD_ASSETS_BUSY", false));
  },

  updateBulk: ({ commit, rootState, getters}, { 
    projectUid, 
    articleIds, 
    changes,
    forceUpdate,
    toAllRecords = {
      status: false, filters: {}, total: 0
    }
  }) => {
    commit("UPDATE_BULK_ARTICLES_BUSY", true);

    const { accessToken } = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    const authId = localStorage.getLocalStorage('request_auth_id');

    let body = {
      articleIds,
      toAllRecords,
      confirm: forceUpdate,
      authId,
      ...changes
    }

    body = bulkDataToSend(body, 'articleIds');

    return new Promise((resolve, reject) => {
      Axios.put(`${API_HOST}/analysis/${projectUid}/articles/update-bulk`, body, config)
        .then(
          response => {
            Vue.notify({
              group: "store_notifications",
              type: "success",
              text: i18n.t("messages.articles.success")
            });

            // remove unfiltered articles && update total pagination
            if(changes.status_id && changes.status_id === 1) {
              commit('REMOVE_ARTICLES', articleIds)
              commit('EDIT_PAGINATION_TOTAL', getters.articlesTotal - articleIds.length)
            }
            resolve(response.data);
          },
          error => {
            let data = error;

            if ( 
              _.get(error, 'response.data.message') &&
              error.response.data.status === 400
            ) {
              data = error.response.data;
            } else {
              updateArticlesShowErrors(data);
            }

            commit("UPDATE_BULK_ARTICLES_FAIL", data);

            reject(data);
          }
        )
        .catch(error => {
          console.error(error);
          commit("UPDATE_BULK_ARTICLES_FAIL", error);
          reject(error);
        })
        .finally(() => commit("UPDATE_BULK_ARTICLES_BUSY", false));
    });
  },
  addUserCoder: ({ commit, rootState }, { projectId, row }) => {
    commit("ADD_USER_CODER_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const body = {
      ...row,
    };

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    return Axios.post(`${API_HOST}/analysis/${projectId}/coders/add`, body, config)
      .then(response => {
        commit("ADD_USER_CODER_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("ADD_USER_CODER_FAIL", error);
      })
      .finally(() => commit("ADD_USER_CODER_BUSY", false));
  },
  updateCoderProfile: ({ commit, rootState }, { coderId, payload }) => {
    commit("UPDATE_CODER_PROFILE_BUSY", true);

     const { accessToken } = rootState.user.user.data;

    const body = {
      ...payload,
    };

    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    return Axios.put(`${API_HOST}/analysis/coder/${coderId}/update`, body, config)
      .then(response => {
        commit("UPDATE_CODER_PROFILE_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });

        console.error(error);
        commit("UPDATE_CODER_PROFILE_FAIL", error);
      })
      .finally(() => commit("UPDATE_CODER_PROFILE_BUSY", false));
  },
  getRelationshipLines: ({commit, rootState}, {projectId ,selectedRows}) => {
    commit("LOAD_RELATIONSHIP_LINES_BUSY", true);
    const {accessToken} = rootState.user.user.data;
    const body = {
      articleIds: selectedRows
    }
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };

    return Axios.post(`${API_HOST}/analysis/${projectId}/articles/articles-mention-lines`, body, config)
      .then(response => {
        commit("LOAD_RELATIONSHIP_LINES_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });
        console.error(error);
        commit("LOAD_RELATIONSHIP_LINES_FAIL", error);
      })
      .finally(() => commit("LOAD_RELATIONSHIP_LINES_BUSY", false));
  },

  getSettings: ({commit, rootState}, {projectId}) => {
    commit("LOAD_SETTINGS_BUSY", true);
    const {accessToken} = rootState.user.user.data;
    const config = {
      headers: { Authorization: `Token token=${accessToken}` }
    };
    return Axios.get(`${API_HOST}/analysis/projects/${projectId}/general`, config)
      .then(response => {
        commit("LOAD_SETTINGS_SUCCESS", response.data);
      })
      .catch(error => {
        Vue.notify({
          group: "store_notifications",
          type: "error",
          text: i18n.t("messages.request.error")
        });
        
        console.error(error);
        commit("LOAD_SETTINGS_FAIL", error);
    })
    .finally(() => {
      commit("LOAD_SETTINGS_BUSY", false);
    });
  },
  bulkDeleteMention: ({commit, rootState}, {projectId,articleIds,mention}) => {
    const {accessToken} = rootState.user.user.data;
    const data = {
      articleIds,
      mention
    }
    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      data
    };

    return Axios.delete(`${API_HOST}/analysis/${projectId}/articles/articles-mentions`, config)
    .then(response => {
      commit("LOAD_RELATIONSHIP_LINES_SUCCESS", response.data);
    })
    .catch(error => {
      Vue.notify({
        group: "store_notifications",
        type: "error",
        text: i18n.t("messages.request.error")
      });
      console.error(error);
      commit("LOAD_RELATIONSHIP_LINES_FAIL", error);
    })
    .finally(() => commit("LOAD_RELATIONSHIP_LINES_BUSY", false));
  },

  getArticle: ({ commit, state, rootState }, {
    projectId,
    articleId,
    cancelToken
  }) => {
    commit("LOAD_ARTICLE_BUSY", { isBusy: true, cancelToken });

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
      cancelToken: cancelToken.token
    };

    return Axios.get(`${API_HOST}/analysis/${projectId}/articles/details/${articleId}`, config)
      .then(response => {
        commit("LOAD_ARTICLE_SUCCESS", response.data);
      })
      .catch(error => {
        if (error.message !== 'aborted') {
          Vue.notify({
            group: "store_notifications",
            type: "error",
            text: i18n.t("messages.request.error")
          });

          console.error(error);
          commit("LOAD_ARTICLE_FAIL", error);
        }
      })
      .finally(() => {
        commit("LOAD_ARTICLE_BUSY", { isBusy: false });
      });
  },

  aiCodeArticles: ({ commit, rootState }, { projectId, articleIds, model_ids, filters, priority_queue }) => {
    commit("AI_CODE_BUSY", true);

    const { accessToken } = rootState.user.user.data;

    const config = {
      headers: { Authorization: `Token token=${accessToken}` },
    };

    const body = { articleIds, filters: camelToSnack(filters), model_ids, priority_queue }

    return Axios.post(`${API_HOST}/analysis/${projectId}/ai_coding/articles`, body, config).catch(error => {
      Vue.notify({
        group: "store_notifications",
        type: "error",
        text: i18n.t("messages.request.error")
      });
    }).finally(() => {
      commit("AI_CODE_BUSY", false);
    });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
